import type { SelectValuesFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { type DialogProps, Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '@packages/shared/src/ui/Dialog/DialogHeader/DialogHeader';
import { useRouter } from 'next/navigation';
import { useIntl } from 'react-intl';
import type { SetSelectedVariation } from '../../types/types';
import { useVariationFormattedLink } from '../utils/useVariationFormattedLink';
import { variationSelectionDialogMessages } from './messages';
import { VariationRow } from './VariationRow/VariationRow';

export type VariationSelectionDialogProps = {
  dimensionData: SelectValuesFragmentFragment;
  open: boolean;
  onClose: NonNullable<DialogProps['onClose']>;
  onChange?: SetSelectedVariation;
};

/**
 * VariationSelectionDialog component
 *
 * dialog to select a different variation
 * */
export const VariationSelectionDialog = ({
  dimensionData,
  open,
  onClose,
  onChange,
}: VariationSelectionDialogProps) => {
  const { formatMessage } = useIntl();
  const hasImage = dimensionData.values.some(
    (value) => value.image !== null || value.rgb !== null || value.variationMainImage !== null,
  );
  const { push } = useRouter();
  const createVariationLink = useVariationFormattedLink();

  const clickHandler = (sku: SelectValuesFragmentFragment['values'][number]['sku']) => {
    // why is the sku nullable?!
    if (sku) {
      if (onChange) {
        onChange(sku);
      } else {
        push(createVariationLink(sku));
      }

      onClose();
    }
  };

  return (
    <Dialog isOpen={open} onClose={onClose} className="w-full max-w-[600px]">
      <DialogHeader onClose={onClose}>
        {formatMessage(variationSelectionDialogMessages.headline, {
          dimension: dimensionData.displayName,
        })}
      </DialogHeader>
      <DialogContent className="p-0">
        <ul className="list-none">
          {dimensionData.values.map((value) => (
            <li key={value.sku}>
              <VariationRow
                value={value}
                hasImage={hasImage}
                clickHandler={clickHandler}
                key={value.sku}
              />
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
